import React, { createContext, useCallback, useContext } from 'react';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { eLocalStorageKeys } from 'src/constants/AppConstants';

const DEFAULT_PANEL_WIDTH = 400;
const MIN_PANEL_WIDTH = 400;
const MAX_PANEL_WIDTH = 800;

interface AskQuickContextValue {
  isOpen: boolean;
  togglePanel: () => void;
  closePanel: () => void;
  panelSize: number;
  onPanelResize: (size: number) => void;
  minPanelSize: number;
  maxPanelSize: number;
}

const AskQuickContext = createContext<AskQuickContextValue>({
  isOpen: false,
  togglePanel: () => {},
  closePanel: () => {},
  panelSize: DEFAULT_PANEL_WIDTH,
  onPanelResize: () => {},
  minPanelSize: MIN_PANEL_WIDTH,
  maxPanelSize: MAX_PANEL_WIDTH
});

export const AskQuickProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = usePersistedState(eLocalStorageKeys.AskQuickPanelOpen, false);
  const [panelSize, setPanelSize] = usePersistedState(eLocalStorageKeys.AskQuickPanelWidth, DEFAULT_PANEL_WIDTH);

  const togglePanel = useCallback(() => setIsOpen((prev: boolean) => !prev), [setIsOpen]);
  const closePanel = useCallback(() => setIsOpen(false), [setIsOpen]);
  const onPanelResize = useCallback((size: number) => setPanelSize(size), [setPanelSize]);

  return (
    <AskQuickContext.Provider
      value={{ isOpen, togglePanel, closePanel, panelSize, onPanelResize, minPanelSize: MIN_PANEL_WIDTH, maxPanelSize: MAX_PANEL_WIDTH }}
    >
      {children}
    </AskQuickContext.Provider>
  );
};

export const useAskQuick = () => useContext(AskQuickContext);
